import * as React from "react";
import {
  ChevronDown,
  Clock,
  CheckCircle2,
  AlertCircle,
  Activity,
} from "lucide-react";
import { cn } from "../lib/utils";

function getDayLabel(
  date,
  { locale = "en", todayLabel = "Today", yesterdayLabel = "Yesterday" } = {}
) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const diffTime = today.getTime() - eventDate.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return todayLabel;
  if (diffDays === 1) return yesterdayLabel;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatRelativeTime(date, locale = "en") {
  const now = Date.now();
  const diffSec = Math.floor((now - date.getTime()) / 1000);

  if (typeof Intl !== "undefined" && Intl.RelativeTimeFormat) {
    try {
      const rtf = new Intl.RelativeTimeFormat(locale, {
        numeric: "auto",
        style: "narrow",
      });
      if (Math.abs(diffSec) < 60) {
        return rtf.format(-Math.max(0, diffSec), "second");
      }
      const diffMin = Math.floor(diffSec / 60);
      if (Math.abs(diffMin) < 60) {
        return rtf.format(-diffMin, "minute");
      }
      const diffHours = Math.floor(diffMin / 60);
      if (Math.abs(diffHours) < 24) {
        return rtf.format(-diffHours, "hour");
      }
      const diffDays = Math.floor(diffHours / 24);
      if (Math.abs(diffDays) < 7) {
        return rtf.format(-diffDays, "day");
      }
    } catch {
      // Fallback below
    }
  }

  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const toneStyles = {
  success:
    "bg-success/15 text-success border-success/35",
  danger:
    "bg-destructive/15 text-destructive border-destructive/35",
  warning:
    "bg-warning/15 text-warning border-warning/35",
  primary: "bg-primary/15 text-primary border-primary/35",
  neutral: "bg-muted text-muted-foreground border-border",
};

const AuditTimeline = React.forwardRef(
  (
    {
      events = [],
      groupByDay = true,
      maxHeight,
      defaultExpanded = [],
      locale = "en",
      todayLabel = "Today",
      yesterdayLabel = "Yesterday",
      activityLabel = "Audit Log",
      emptyTitle = "No audit events recorded",
      emptySubtitle = "Audit milestones and events will appear here.",
      formatEventCount,
      className,
      ...props
    },
    ref
  ) => {
    const [expandedIds, setExpandedIds] = React.useState(
      new Set(defaultExpanded)
    );

    const toggleExpand = (id) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };

    // Sort deterministically: timestamp DESC, then ID secondary key
    const sortedEvents = React.useMemo(() => {
      return [...events].sort((a, b) => {
        const timeA = new Date(a.at || a.timestamp || a.createdAt || 0).getTime();
        const timeB = new Date(b.at || b.timestamp || b.createdAt || 0).getTime();
        if (timeB !== timeA) return timeB - timeA;
        return String(b.id || "").localeCompare(String(a.id || ""));
      });
    }, [events]);

    // Group by Day
    const dayGroups = React.useMemo(() => {
      if (!groupByDay) {
        return [{ label: activityLabel, events: sortedEvents }];
      }

      const groups = new Map();
      sortedEvents.forEach((ev) => {
        const d = new Date(ev.at || ev.timestamp || ev.createdAt || Date.now());
        const validDate = isNaN(d.getTime()) ? new Date() : d;
        const key = getDayLabel(validDate, {
          locale,
          todayLabel,
          yesterdayLabel,
        });

        if (!groups.has(key)) {
          groups.set(key, { label: key, events: [] });
        }
        groups.get(key).events.push(ev);
      });

      return Array.from(groups.values());
    }, [sortedEvents, groupByDay, locale, todayLabel, yesterdayLabel, activityLabel]);

    if (events.length === 0) {
      return (
        <div
          ref={ref}
          data-testid="audit-timeline-empty"
          className={cn(
            "flex flex-col items-center justify-center p-8 text-center text-muted-foreground",
            className
          )}
          {...props}
        >
          <Activity className="size-8 opacity-40 mb-2" />
          <p className="text-xs font-medium text-foreground">{emptyTitle}</p>
          <p className="text-caption text-muted-foreground mt-0.5">
            {emptySubtitle}
          </p>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        data-testid="audit-timeline"
        className={cn("space-y-5", className)}
        style={{ maxHeight, overflowY: maxHeight ? "auto" : undefined }}
        {...props}
      >
        {dayGroups.map((group) => {
          const countLabel = formatEventCount
            ? formatEventCount(group.events.length)
            : `${group.events.length} event${group.events.length > 1 ? "s" : ""}`;

          return (
            <div key={group.label} className="space-y-3" data-testid="audit-timeline-group">
              {/* Day Header */}
              {groupByDay && (
                <div className="sticky top-0 z-10 flex items-center justify-between rounded-lg bg-muted/60 backdrop-blur-md px-3 py-1.5 text-xs font-semibold text-muted-foreground border border-border/80">
                  <span>{group.label}</span>
                  <span className="font-mono text-caption text-muted-foreground tabular-nums">
                    {countLabel}
                  </span>
                </div>
              )}

              {/* Events for this day */}
              <div className="relative pl-6 space-y-3 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-px before:bg-border">
                {group.events.map((ev) => {
                  const date = new Date(
                    ev.at || ev.timestamp || ev.createdAt || Date.now()
                  );
                  const validDate = isNaN(date.getTime()) ? new Date() : date;
                  const isExpanded = expandedIds.has(ev.id);
                  const hasExpandableDetail = Boolean(
                    ev.detail || ev.diff || ev.metadata
                  );
                  const tone = ev.tone || "neutral";

                  return (
                    <div
                      key={ev.id}
                      data-testid="audit-timeline-item"
                      className="relative group"
                    >
                      {/* Node Dot / Icon */}
                      <div
                        className={cn(
                          "absolute -left-[22px] top-2.5 flex size-5 items-center justify-center rounded-full border shadow-2xs transition-transform duration-150 group-hover:scale-110",
                          toneStyles[tone] || toneStyles.neutral
                        )}
                      >
                        {ev.icon ? (
                          ev.icon
                        ) : tone === "success" ? (
                          <CheckCircle2 className="size-3" />
                        ) : tone === "danger" ? (
                          <AlertCircle className="size-3" />
                        ) : (
                          <div className="size-1.5 rounded-full bg-current" />
                        )}
                      </div>

                      {/* Event Content Card */}
                      <div
                        role={hasExpandableDetail ? "button" : undefined}
                        tabIndex={hasExpandableDetail ? 0 : undefined}
                        aria-expanded={hasExpandableDetail ? isExpanded : undefined}
                        onKeyDown={
                          hasExpandableDetail
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  toggleExpand(ev.id);
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          "rounded-xl border border-border bg-card p-3.5 shadow-2xs transition-all",
                          hasExpandableDetail &&
                            "cursor-pointer hover:border-primary/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                        )}
                        onClick={() => hasExpandableDetail && toggleExpand(ev.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2.5 min-w-0">
                            {ev.avatar && (
                              <img
                                src={ev.avatar}
                                alt={ev.actor || "User"}
                                className="size-6 rounded-full object-cover border border-border shrink-0 mt-0.5"
                              />
                            )}
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {ev.actor && (
                                  <span className="text-xs font-semibold text-foreground">
                                    {ev.actor}
                                  </span>
                                )}
                                <span className="text-xs font-medium text-foreground">
                                  {ev.title || ev.action}
                                </span>
                                {ev.badge && (
                                  <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-overline-size font-mono font-medium text-muted-foreground border border-border/80">
                                    {ev.badge}
                                  </span>
                                )}
                              </div>
                              {ev.description && (
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                  {ev.description}
                                </p>
                              )}
                              {ev.meta && (
                                <p className="truncate text-caption text-muted-foreground mt-0.5 font-mono">
                                  {ev.meta}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground text-caption font-mono tabular-nums">
                            <Clock className="size-3" />
                            <span>{formatRelativeTime(validDate, locale)}</span>
                            {hasExpandableDetail && (
                              <ChevronDown
                                className={cn(
                                  "size-3.5 transition-transform duration-200",
                                  isExpanded && "rotate-180"
                                )}
                              />
                            )}
                          </div>
                        </div>

                        {/* Expandable Details */}
                        {hasExpandableDetail && isExpanded && (
                          <div
                            data-testid="audit-timeline-detail"
                            className="mt-2.5 pt-2.5 border-t border-border/70 text-xs text-muted-foreground"
                          >
                            {typeof ev.detail === "string" ? (
                              <p className="whitespace-pre-wrap font-sans text-foreground/90">
                                {ev.detail}
                              </p>
                            ) : (
                              ev.detail
                            )}

                            {ev.metadata && typeof ev.metadata === "object" && (
                              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 rounded-lg bg-muted/50 border border-border/60 p-2.5 font-mono text-caption">
                                {Object.entries(ev.metadata).map(([k, v]) => (
                                  <div key={k} className="truncate">
                                    <span className="text-muted-foreground">
                                      {k}:{" "}
                                    </span>
                                    <span className="text-foreground">
                                      {typeof v === "object"
                                        ? JSON.stringify(v)
                                        : String(v)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);
AuditTimeline.displayName = "AuditTimeline";

const AuditTimelineEventNode = ({ tone = "neutral", icon, className }) => (
  <div
    className={cn(
      "flex size-5 items-center justify-center rounded-full border shadow-2xs",
      toneStyles[tone] || toneStyles.neutral,
      className
    )}
  >
    {icon}
  </div>
);
AuditTimelineEventNode.displayName = "AuditTimelineEventNode";

const AuditTimelineRow = ({ className, children, ...props }) => (
  <div className={cn("relative group pl-6 space-y-3", className)} {...props}>
    {children}
  </div>
);
AuditTimelineRow.displayName = "AuditTimelineRow";

export { AuditTimeline, AuditTimelineEventNode, AuditTimelineRow };
